import type { App } from '@grafikr/components';
import type { Suggest } from '@grafikr/shopify-typescript/type/endpoints/Search';
import type { ProductMedia } from '@grafikr/shopify-typescript/type/liquid/ProductMedia';
import type { Component, JSX } from 'solid-js';
import classNames from 'classnames';
import { fromEvent, throttleTime } from 'rxjs';
import { createEffect, createSignal, For, Show, splitProps } from 'solid-js';

import LazyContent from 'components/reactive/lazy-content';

import formatMoney from 'utils/format-money';
import getDiscounts from 'utils/get-discounts';
import { image } from 'utils/image';
import setUrlParams from 'utils/set-url-params';
import stringToNode from 'utils/string-to-node';

type Props = {
  app: App;
  blocks: { cta: string; url: string; image: ProductMedia }[];
} & Omit<JSX.HTMLAttributes<HTMLFormElement>, 'action' | 'ref'>;

const Content: Component<Props> = (props) => {
  const [{ app, blocks, class: className }, all] = splitProps(props, ['app', 'blocks', 'class']);
  const [result, setResult] = createSignal<Suggest>(null);
  const discounts = getDiscounts();

  let form: HTMLFormElement;
  let searchInput: HTMLInputElement;

  createEffect(() => {
    const event = async () => {
      const data = Array.from(new FormData(form), ([key, value]) => [
        key,
        typeof value === 'string' ? value : value.name,
      ]);

      const url = new URL(`${theme.routes.predictiveSearch}.json`, window.location.origin);

      setUrlParams(url, {
        'resources[limit]': '4',
        'resources[limit_scope]': 'each',
        'resources[options][fields]': [
          'body',
          'product_type',
          'tag',
          'title',
          'variants.barcode',
          'variants.sku',
          'variants.title',
        ].join(','),
      });

      data.forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });

      const response = await fetch(url);

      if (response.ok) {
        setResult(await response.json());
      } else {
        setResult(undefined);
      }
    };

    fromEvent(searchInput, 'keyup').pipe(throttleTime(300)).subscribe(event);
  });

  return (
    <form
      class={classNames('min-h-full flex-col flex', className)}
      {...all}
      action={theme.routes.search}
      ref={form}
    >
      <div class="flex">
        <div class="flex-1" data-close={true} />

        <div class="w-full flex-col flex" classList={{ 'max-w-4xl': !result() }}>
          <div class="mx-auto mb-10 mt-5 w-full max-w-md px-px lg:mt-20">
            <div class="form-icon">
              <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 13 13">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M1.4 5.263a4.063 4.063 0 1 1 8.125 0 4.063 4.063 0 0 1-8.125 0ZM5.463 0A5.263 5.263 0 1 0 8.23 9.74l.199.2 2.543 2.543.424.424.849-.849-.425-.424-2.543-2.543-.1-.1A5.263 5.263 0 0 0 5.463 0Z"
                  fill="currentColor"
                />
              </svg>

              <input
                ref={searchInput}
                class="form-input--lg form-input rounded-full border-b-0 bg-glass-black/8 placeholder:text-black/40 hover:bg-glass-black/8"
                placeholder={theme.strings.search.placeholder}
                name="q"
              />
            </div>
          </div>

          <Show when={result()}>
            <div class="w-full flex-1 overflow-auto overscroll-contain grid lg:grid-cols-4">
              <div>
                <Show when={result().resources.results.collections.length}>
                  <p class="h4 mb-4 opacity-40">{theme.strings.search.category}</p>

                  <ul class="heading mb-10 space-y-4 text-lg">
                    <For each={result().resources.results.collections}>
                      {(collection) => (
                        <li>
                          <a href={collection.url} class={'underline-offset-2 hover:underline'}>
                            {collection.title}
                          </a>
                        </li>
                      )}
                    </For>
                  </ul>
                </Show>

                <button class="button button--dark button--outline hidden lg:block">
                  {theme.strings.search.showAll}
                </button>
              </div>

              <div class="lg:col-span-3">
                <Show when={result().resources.results.products.length}>
                  <ul class="mb-12 grid-cols-2 gap-4 grid lg:grid-cols-4">
                    <For each={result().resources.results.products}>
                      {(product) => {
                        const url = new URL(product.url, window.location.origin);
                        url.searchParams.set('view', 'item-simple');

                        // Hide products in a private discount
                        if (
                          discounts.some(
                            (discount) =>
                              discount.private && product.tags.includes(discount.product_tag),
                          )
                        ) {
                          return undefined;
                        }

                        return (
                          <li>
                            <a href={product.url} aria-label={product.title}>
                              <LazyContent url={url} app={app}>
                                <div class="product-item animate-pulse @container">
                                  <div class="aspect-h-3 aspect-w-2" />

                                  <div class="px-4 pt-4" data-content="">
                                    <div class="mb-3 flex-wrap justify-between gap-2 flex">
                                      <p class="w-full font-bold @xs:w-auto">{product.title}</p>

                                      <p class="w-full @xs:w-auto">
                                        {formatMoney(product.price)
                                          .removeTrailingZeros()
                                          .toString()}
                                      </p>
                                    </div>

                                    <div class="h-[1em] items-center gap-2 pb-[2px] flex">
                                      <button class="swatch bg-gray-200" />
                                      <button class="swatch bg-gray-200" />
                                      <button class="swatch bg-gray-200" />
                                    </div>
                                  </div>
                                </div>
                              </LazyContent>
                            </a>
                          </li>
                        );
                      }}
                    </For>
                  </ul>

                  <div class="text-center lg:hidden">
                    <button class="button button--dark button--outline">
                      {theme.strings.search.showAll}
                    </button>
                  </div>
                </Show>
              </div>
            </div>
          </Show>

          <Show when={!result()}>
            <div class="w-full flex-1 overflow-auto overscroll-contain lg:mt-24">
              <p class="h4 mb-5">{theme.strings.search.trending}</p>

              <ul class="grid-cols-3 gap-y-4 grid lg:flex">
                <For each={blocks}>
                  {(block) => (
                    <li class="flex-1">
                      <a
                        href={block.url}
                        aria-label={block.cta}
                        class="mb-2 block -outline-offset-4"
                      >
                        <div class="aspect-h-3 aspect-w-2">{stringToNode(image(block.image))}</div>
                      </a>

                      <p class="text-center">{block.cta}</p>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        </div>

        <div class="flex-1" data-close={true} />
      </div>

      <div class="flex-1" data-close={true} />
    </form>
  );
};

export default Content;
